/**
 * Progress persistence.
 * ---------------------------------------------------------------------------
 * TypeSwift has no accounts and no backend, so a child's journey lives in this
 * browser. Everything is funnelled through the small API below rather than
 * touching localStorage directly, for three reasons:
 *
 *   1. One place to version and migrate the schema when the curriculum grows.
 *   2. One place that is SSR-safe (every read returns a valid empty store on
 *      the server, so nothing has to guard `typeof window`).
 *   3. One seam to swap for a real API later — implement the same functions
 *      against fetch() and nothing above this file changes.
 *
 * Privacy note: this is children's data. It never leaves the device, it is
 * never sent to an analytics endpoint, and the only identifying field is a
 * display name the child types themselves.
 */
import type {
  AvatarConfig,
  KidProfile,
  KidSettings,
  KidsStore,
  LessonRecord,
  LessonScore,
  SessionRecord,
} from '../types';
import { ALL_LESSONS, getLesson } from '../data/curriculum';

const STORAGE_KEY = 'typeswift_kids_v1';
const SCHEMA_VERSION = 1;
/** Enough for well over a year of daily practice; keeps the payload small. */
const MAX_SESSIONS = 500;

/* ─────────────────────────────────────────────────────────────── defaults ── */

export const DEFAULT_SETTINGS: KidSettings = {
  sound: true,
  voice: false,
  volume: 0.5,
  scheme: 'light',
  motion: 'full',
  reading: 'default',
  contrast: 'default',
  vision: 'default',
  keyboard: true,
  hands: true,
};

export const DEFAULT_AVATAR: AvatarConfig = {
  body: 'round',
  skin: '#F5C99B',
  hair: 'short',
  hairColor: '#3A2A1D',
  eyes: 'happy',
  accessory: 'none',
  background: 'mint',
  pet: 'none',
};

export function emptyStore(): KidsStore {
  return { version: SCHEMA_VERSION, activeProfileId: null, profiles: [], classroom: null };
}

/** Local calendar day key — the unit streaks, charts and reports all use. */
export function dayKey(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function daysBetween(a: string, b: string): number {
  const toUtc = (s: string) => {
    const [y, m, d] = s.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((toUtc(b) - toUtc(a)) / 86_400_000);
}

let idCounter = 0;
/** Collision-resistant without crypto — profiles are per-device and few. */
function newId(prefix: string): string {
  idCounter += 1;
  return `${prefix}_${Date.now().toString(36)}${idCounter.toString(36)}`;
}

export function createProfile(
  name: string,
  ageBand: KidProfile['ageBand'] = 'unspecified'
): KidProfile {
  return {
    id: newId('kid'),
    name: name.trim().slice(0, 24) || 'Typist',
    ageBand,
    created: new Date().toISOString(),
    avatar: { ...DEFAULT_AVATAR },
    xp: 0,
    coins: 25, // a small starting purse so the shop is explorable on day one
    streak: { current: 0, longest: 0, lastDay: null, weekDays: [] },
    lessons: {},
    keyStats: {},
    sessions: [],
    achievements: {},
    unlocked: [],
    equipped: { keyboardSkin: 'classic', theme: 'default', title: '' },
    chestDay: null,
    settings: { ...DEFAULT_SETTINGS },
    assigned: [],
  };
}

/* ────────────────────────────────────────────────────────────── read/write ── */

/** Migrate a store read from disk to the current schema. Never throws. */
function migrate(raw: unknown): KidsStore {
  if (!raw || typeof raw !== 'object') return emptyStore();
  const store = raw as Partial<KidsStore>;
  if (!Array.isArray(store.profiles)) return emptyStore();

  // Everything read from disk is genuinely `unknown`, whatever the declared
  // type says — a profile written by an older build, or hand-edited, may be
  // missing any field. Typing it as DeepPartial makes the compiler enforce
  // that every access here is defensive.
  const stored = store.profiles as unknown as Partial<KidProfile>[];

  const profiles: KidProfile[] = stored.map((p) => {
    const base = createProfile(p?.name ?? 'Typist');
    return {
      ...base,
      ...p,
      // Nested objects need an explicit merge: a profile saved before a new
      // setting existed must pick up its default, not `undefined`. Defaults
      // come first so anything saved wins, and anything absent falls back.
      settings: { ...base.settings, ...(p?.settings ?? {}) },
      avatar: { ...base.avatar, ...(p?.avatar ?? {}) },
      equipped: { ...base.equipped, ...(p?.equipped ?? {}) },
      streak: { ...base.streak, ...(p?.streak ?? {}) },
      lessons: p?.lessons ?? {},
      keyStats: p?.keyStats ?? {},
      sessions: Array.isArray(p?.sessions) ? p.sessions : [],
      achievements: p?.achievements ?? {},
      unlocked: Array.isArray(p?.unlocked) ? p.unlocked : [],
      assigned: Array.isArray(p?.assigned) ? p.assigned : [],
      id: p?.id ?? base.id,
    };
  });

  const activeProfileId =
    store.activeProfileId && profiles.some((p) => p.id === store.activeProfileId)
      ? store.activeProfileId
      : (profiles[0]?.id ?? null);

  return {
    version: SCHEMA_VERSION,
    activeProfileId,
    profiles,
    classroom: store.classroom ?? null,
  };
}

export function loadStore(): KidsStore {
  if (typeof window === 'undefined') return emptyStore();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyStore();
    return migrate(JSON.parse(raw));
  } catch {
    // Corrupt or blocked storage (private mode, quota, hostile extension) —
    // degrade to an in-memory session rather than breaking the app.
    return emptyStore();
  }
}

export function saveStore(store: KidsStore): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* Quota exceeded or storage disabled — progress stays in memory. */
  }
}

export function getActiveProfile(store: KidsStore): KidProfile | null {
  return store.profiles.find((p) => p.id === store.activeProfileId) ?? null;
}

/** Apply `fn` to one profile and return a new store. Pure. */
export function updateProfile(
  store: KidsStore,
  profileId: string,
  fn: (p: KidProfile) => KidProfile
): KidsStore {
  return {
    ...store,
    profiles: store.profiles.map((p) => (p.id === profileId ? fn(p) : p)),
  };
}

/* ──────────────────────────────────────────────────────── recording a run ── */

/** Roll the streak forward for a practice session on `today`. */
export function advanceStreak(profile: KidProfile, today: string): KidProfile['streak'] {
  const { streak } = profile;
  if (streak.lastDay === today) return streak;

  const gap = streak.lastDay ? daysBetween(streak.lastDay, today) : Infinity;
  const current = gap === 1 ? streak.current + 1 : 1;

  // Keep only the last seven days for the week strip.
  const weekDays = Array.from(new Set([...streak.weekDays, today]))
    .filter((d) => daysBetween(d, today) < 7)
    .sort();

  return {
    current,
    longest: Math.max(streak.longest, current),
    lastDay: today,
    weekDays,
  };
}

export interface RecordResult {
  profile: KidProfile;
  /** True when this run beat the child's previous star count for the lesson. */
  improved: boolean;
  /** True the very first time a lesson is completed. */
  firstClear: boolean;
  /** Achievement ids newly earned by this run. */
  newAchievements: string[];
  /** Rank level before the run, so the UI can celebrate a rank-up. */
  previousRankXp: number;
}

/**
 * Fold one finished lesson into a profile: records, key stats, session log,
 * XP, coins and streak. Pure — the caller persists the returned profile.
 */
export function recordLesson(
  profile: KidProfile,
  lessonId: string,
  score: LessonScore,
  keyStats: Record<string, { correct: number; wrong: number }>,
  now: Date = new Date()
): RecordResult {
  const lesson = getLesson(lessonId);
  const previous: LessonRecord | undefined = profile.lessons[lessonId];
  const firstClear = !previous?.completed;
  const improved = score.stars > (previous?.stars ?? 0);

  const record: LessonRecord = {
    stars: Math.max(score.stars, previous?.stars ?? 0) as LessonRecord['stars'],
    bestWpm: Math.max(score.netWpm, previous?.bestWpm ?? 0),
    bestAccuracy: Math.max(score.accuracy, previous?.bestAccuracy ?? 0),
    attempts: (previous?.attempts ?? 0) + 1,
    lastPlayed: now.toISOString(),
    completed: true,
  };

  const mergedKeyStats = { ...profile.keyStats };
  for (const [char, stat] of Object.entries(keyStats)) {
    const prev = mergedKeyStats[char] ?? { correct: 0, wrong: 0 };
    mergedKeyStats[char] = {
      correct: prev.correct + stat.correct,
      wrong: prev.wrong + stat.wrong,
    };
  }

  const session: SessionRecord = {
    day: dayKey(now),
    iso: now.toISOString(),
    lessonId,
    levelId: lesson?.levelId ?? '',
    wpm: score.netWpm,
    accuracy: score.accuracy,
    seconds: score.seconds,
    chars: score.chars,
    mistakes: score.mistakes,
    stars: score.stars,
    xp: score.xp,
  };

  // Replaying a cleared lesson still earns, but at a third of the value — the
  // journey should always be the most rewarding path forward.
  const multiplier = firstClear ? 1 : 0.34;
  const xpGain = Math.max(2, Math.round(score.xp * multiplier));
  const coinGain = Math.max(1, Math.round(score.coins * multiplier));

  const next: KidProfile = {
    ...profile,
    xp: profile.xp + xpGain,
    coins: profile.coins + coinGain,
    lessons: { ...profile.lessons, [lessonId]: record },
    keyStats: mergedKeyStats,
    sessions: [...profile.sessions, session].slice(-MAX_SESSIONS),
    streak: advanceStreak(profile, dayKey(now)),
  };

  return {
    profile: next,
    improved,
    firstClear,
    newAchievements: [],
    previousRankXp: profile.xp,
  };
}

/* ───────────────────────────────────────────────────────────── unlocking ─── */

/**
 * A lesson is unlocked when the previous lesson in course order is completed.
 * The first lesson is always open, and a teacher assignment opens its lesson
 * regardless of position so a class can start anywhere.
 */
export function isLessonUnlocked(profile: KidProfile | null, lessonId: string): boolean {
  const index = ALL_LESSONS.findIndex((l) => l.id === lessonId);
  if (index <= 0) return true;
  if (!profile) return false;
  if (profile.assigned.includes(lessonId)) return true;
  return Boolean(profile.lessons[ALL_LESSONS[index - 1].id]?.completed);
}

export function isLevelUnlocked(profile: KidProfile | null, levelId: string): boolean {
  const first = ALL_LESSONS.find((l) => l.levelId === levelId);
  return first ? isLessonUnlocked(profile, first.id) : false;
}

/** The lesson the child should do next — the single "Continue" destination. */
export function nextUpLesson(profile: KidProfile | null): string {
  if (!profile) return ALL_LESSONS[0].id;
  const assigned = profile.assigned.find((id) => !profile.lessons[id]?.completed);
  if (assigned) return assigned;
  const pending = ALL_LESSONS.find((l) => !profile.lessons[l.id]?.completed);
  return pending?.id ?? ALL_LESSONS[ALL_LESSONS.length - 1].id;
}

export function completedCount(profile: KidProfile | null): number {
  if (!profile) return 0;
  return ALL_LESSONS.filter((l) => profile.lessons[l.id]?.completed).length;
}

export function totalStars(profile: KidProfile | null): number {
  if (!profile) return 0;
  return Object.values(profile.lessons).reduce((sum, r) => sum + r.stars, 0);
}

export const MAX_STARS = ALL_LESSONS.length * 3;

/** True when every lesson in the course is complete — unlocks the certificate. */
export function hasFinishedCourse(profile: KidProfile | null): boolean {
  return completedCount(profile) === ALL_LESSONS.length;
}
